import React, { Fragment, useEffect } from "react";
import { identifierPairs } from "../../config/statutoryIdentifiers";
import SEO from "../../components/SEO/SEO";
import { LegalPageLayout, LegalList, LegalListItem } from "../../components/LegalPageLayout";

const RefundPage: React.FC = () => {
  return (
    <Fragment>
    <SEO title="Refund Policy" description="AXOIX refund policy — 7-day money-back guarantee on all paid plans. Details on how to request a refund." path="/refund" />
    <LegalPageLayout
      pageTitle="Refund Policy"
      pageSub="Refund"
      lastUpdated="June 22, 2026"
    >
      <h2 className="details-content-title mb-15">1. Our Commitment</h2>
      <p>
        At <strong>Axoix Technologies Private Limited</strong>, we want you to be completely satisfied with
        your AXOIX subscription. We offer a <strong>7-day money-back guarantee</strong>{" "}
        for new subscribers who are not satisfied with our Services.
      </p>

      <div className="postabmin_block xb-border mt-40 mb-40">
        <div className="admin_content">
          <h4 className="admin_name">7-Day Money-Back Guarantee</h4>
          <p>
            If you're not happy with AXOIX within the first 7 days of your
            subscription, we'll give you a full refund. No questions asked.
          </p>
        </div>
      </div>

      <h2 className="details-content-title mb-15 mt-50">2. Eligibility for Refund</h2>

      <h3 className="item_details_info_heading mb-20 mt-30">2.1 Eligible for Full Refund</h3>
      <LegalList>
        <LegalListItem><strong>New subscribers</strong> who request a refund within 7 days of their first payment</LegalListItem>
        <LegalListItem><strong>Service outages</strong> lasting more than 24 consecutive hours (pro-rated refund)</LegalListItem>
        <LegalListItem><strong>Billing errors</strong> where you were charged incorrectly</LegalListItem>
        <LegalListItem><strong>Duplicate charges</strong> for the same subscription period</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">2.2 Not Eligible for Refund</h3>
      <LegalList>
        <LegalListItem>Refund requests made after the 7-day period for new subscribers</LegalListItem>
        <LegalListItem>Renewal payments (you can cancel before renewal to avoid charges)</LegalListItem>
        <LegalListItem>Subscriptions cancelled mid-cycle (except for billing errors)</LegalListItem>
        <LegalListItem>Accounts terminated for Terms of Service violations</LegalListItem>
        <LegalListItem>Add-on purchases (unless defective or not as described)</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">3. How to Request a Refund</h2>

      <h3 className="item_details_info_heading mb-20 mt-30">Step 1: Contact Us</h3>
      <p>Send a refund request to one of the following:</p>
      <LegalList>
        <LegalListItem><strong>Email:</strong> <a href="mailto:billing@axoix.com">billing@axoix.com</a></LegalListItem>
        <LegalListItem><strong>Support:</strong> <a href="mailto:hello@axoix.com">hello@axoix.com</a></LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">Step 2: Provide Required Information</h3>
      <p>Include the following in your refund request:</p>
      <LegalList>
        <LegalListItem>Your registered email address</LegalListItem>
        <LegalListItem>Subscription plan name</LegalListItem>
        <LegalListItem>Date of purchase</LegalListItem>
        <LegalListItem>Reason for refund (optional, but helps us improve)</LegalListItem>
        <LegalListItem>Transaction ID or invoice number (if available)</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">Step 3: Wait for Confirmation</h3>
      <p>
        We will review your request and respond within <strong>2 business days</strong>.
        If approved, your refund will be processed within <strong>5-7 business days</strong>.
      </p>

      <h2 className="details-content-title mb-15 mt-50">4. Refund Processing</h2>

      <h3 className="item_details_info_heading mb-20 mt-30">4.1 Processing Time</h3>
      <LegalList>
        <LegalListItem><strong>Credit/Debit Card:</strong> 5-7 business days</LegalListItem>
        <LegalListItem><strong>UPI / Net Banking:</strong> 3-5 business days</LegalListItem>
        <LegalListItem><strong>PayPal:</strong> 3-5 business days</LegalListItem>
        <LegalListItem><strong>Bank Transfer:</strong> 7-10 business days</LegalListItem>
      </LegalList>

      <h3 className="item_details_info_heading mb-20 mt-30">4.2 Refund Amount</h3>
      <LegalList>
        <LegalListItem><strong>Full Refund:</strong> For eligible requests within the 7-day period</LegalListItem>
        <LegalListItem><strong>Pro-rated Refund:</strong> For service outages exceeding 24 hours</LegalListItem>
        <LegalListItem><strong>No Refund:</strong> For ineligible requests as described above</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">5. Cancellation vs. Refund</h2>
      <p><strong>Cancellation</strong> and <strong>refund</strong> are different:</p>
      <LegalList>
        <LegalListItem><strong>Cancellation:</strong> Your subscription will not renew. You can continue using the service until the end of your current billing period.</LegalListItem>
        <LegalListItem><strong>Refund:</strong> Your subscription is terminated immediately, and you receive your money back (if eligible).</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">6. Exceptional Circumstances</h2>
      <p>
        We understand that exceptional circumstances may arise. If you have a special
        situation not covered by this policy, please contact us, and we'll do our best
        to help.
      </p>
      <p>Examples of exceptional circumstances we may consider:</p>
      <LegalList>
        <LegalListItem>Medical emergencies</LegalListItem>
        <LegalListItem>Natural disasters affecting your business</LegalListItem>
        <LegalListItem>Significant service issues on our end</LegalListItem>
      </LegalList>

      <h2 className="details-content-title mb-15 mt-50">7. Contact Us</h2>
      <p>For refund requests or billing questions, contact us at:</p>
      <LegalList>
        <LegalListItem><strong>Billing:</strong> <a href="mailto:billing@axoix.com">billing@axoix.com</a></LegalListItem>
        <LegalListItem><strong>Support:</strong> <a href="mailto:hello@axoix.com">hello@axoix.com</a></LegalListItem>
        <LegalListItem><strong>General:</strong> <a href="mailto:rajat@axoix.com">rajat@axoix.com</a></LegalListItem>
      </LegalList>

      <div className="postabmin_block xb-border mt-50">
        <div className="admin_content">
          <p>
            <strong>Remember:</strong> All billing and refunds are processed by{" "}
            <strong>Axoix Technologies Private Limited</strong>. You may see "Axoix Technologies Private Limited" on your bank
            statement for AXOIX subscriptions.
          </p>
          <p className="mt-15" style={{ fontSize: "13px", opacity: 0.8 }}>
            <strong>Registered office:</strong> B-59, Second Floor, Southern Side, Block B,
            Rama Park Road, Uttam Nagar, New Delhi, Delhi - 110059, India.<br />
            {identifierPairs().map(([label, value], i) => (
              <React.Fragment key={label}>
                {i > 0 && <> &nbsp;|&nbsp; </>}
                <strong>{label}:</strong> {value}
              </React.Fragment>
            ))}
          </p>
        </div>
      </div>
    </LegalPageLayout>
    </Fragment>
  );
};

export default RefundPage;
